import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sparkles,
  Send,
  Loader2,
  Plus,
  Trash2,
  MessageSquare,
  Bot,
  User as UserIcon,
} from "lucide-react";

const STARTERS = [
  "Renovation recommendations",
  "Nearby infrastructure & development",
  "Investment pros & cons",
  "Questions to ask at inspection",
];

const TOPICS = [
  { value: "general", label: "General question" },
  { value: "renovation", label: "Renovation advice" },
  { value: "growth", label: "Suburb & growth outlook" },
  { value: "investment", label: "Investment analysis" },
  { value: "inspection", label: "Inspection checklist" },
  { value: "rental", label: "Rental & tenant advice" },
  { value: "property", label: "Specific property (enter address)" },
];

const TOPIC_HINTS = {
  general: "Ask a general property question.",
  renovation: "Ask about cost-effective, value-adding renovations.",
  growth: "Ask about the suburb's growth drivers and outlook.",
  investment: "Ask about the investment case, risks and strategy.",
  inspection: "Get a checklist of what to check at an inspection.",
  rental: "Ask about rentability, tenants and maximising rent.",
  property: "Enter an address above, then ask about that property.",
};

export default function AiAdvisorChat({ reports = [] }) {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [topic, setTopic] = useState("general");
  const [propertyAddress, setPropertyAddress] = useState("");
  const scrollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.get("/research/chat/conversations");
      setConversations(res.data.conversations || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const openConversation = async (conv) => {
    setActiveConv(conv);
    setMessages([]);
    try {
      const res = await api.get(`/research/chat/conversations/${conv.conversation_id}/messages`);
      setMessages(res.data.messages || []);
    } catch (err) {
      toast.error("Could not load conversation.");
    }
  };

  const startNewChat = () => {
    setActiveConv(null);
    setMessages([]);
    setInput("");
  };

  const ensureConversation = async () => {
    if (activeConv) return activeConv;
    const address = topic === "property" ? propertyAddress.trim() || null : null;
    const res = await api.post("/research/chat/conversations", {
      topic,
      property_address: address,
    });
    const conv = res.data;
    setActiveConv(conv);
    setConversations((prev) => [conv, ...prev]);
    return conv;
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    if (topic === "property" && !activeConv && !propertyAddress.trim()) {
      toast.error("Please enter the property address first.");
      return;
    }
    setSending(true);
    // optimistic user message
    const optimistic = { message_id: `tmp_${Date.now()}`, role: "user", content };
    setMessages((prev) => [...prev, optimistic]);
    setInput("");
    try {
      const conv = await ensureConversation();
      const res = await api.post(`/research/chat/conversations/${conv.conversation_id}/message`, {
        content,
      });
      setMessages((prev) => [
        ...prev.filter((m) => m.message_id !== optimistic.message_id),
        res.data.user_message,
        res.data.assistant_message,
      ]);
      fetchConversations();
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.message_id !== optimistic.message_id));
      const msg = err?.response?.data?.detail || "The AI advisor could not respond. Please try again.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const deleteConversation = async (e, conversationId) => {
    e.stopPropagation();
    try {
      await api.delete(`/research/chat/conversations/${conversationId}`);
      setConversations((prev) => prev.filter((c) => c.conversation_id !== conversationId));
      if (activeConv?.conversation_id === conversationId) startNewChat();
      toast.success("Chat deleted.");
    } catch (err) {
      toast.error("Could not delete chat.");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="w-8 h-8 rounded-lg bg-[#547d91]/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#547d91]" />
          </div>
          AI Property Advisor
        </CardTitle>
        <p className="text-sm text-slate-500">
          Ask about renovations, nearby development, investment pros &amp; cons, and what to check at
          inspection. AI guidance only — always verify specifics independently.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
          {/* Sidebar: context + conversations */}
          <div className="space-y-3">
            <select
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={!!activeConv}
              data-testid="advisor-context-select"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white disabled:bg-slate-50 disabled:text-slate-400"
            >
              {TOPICS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>

            {topic === "property" && !activeConv && (
              <Input
                value={propertyAddress}
                onChange={(e) => setPropertyAddress(e.target.value)}
                placeholder="Enter property address"
                data-testid="advisor-property-address"
                className="text-sm"
              />
            )}

            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={startNewChat}
              data-testid="advisor-new-chat"
            >
              <Plus className="w-4 h-4 mr-1" /> New chat
            </Button>

            <div className="space-y-1 max-h-64 overflow-y-auto">
              {conversations.length === 0 ? (
                <p className="text-xs text-slate-400 px-1 py-2">No conversations yet.</p>
              ) : (
                conversations.map((c) => (
                  <div
                    key={c.conversation_id}
                    onClick={() => openConversation(c)}
                    className={`group flex items-center justify-between gap-2 px-2 py-2 rounded-lg cursor-pointer text-sm ${
                      activeConv?.conversation_id === c.conversation_id
                        ? "bg-[#547d91]/10 text-[#547d91]"
                        : "hover:bg-slate-50 text-slate-700"
                    }`}
                    data-testid="advisor-conversation-item"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{c.title}</span>
                    </div>
                    <button
                      onClick={(e) => deleteConversation(e, c.conversation_id)}
                      className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat area */}
          <div className="flex flex-col border border-slate-200 rounded-lg overflow-hidden min-h-[420px]">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50" style={{ maxHeight: "50vh" }}>
              {messages.length === 0 && !sending ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 py-8">
                  <Bot className="w-10 h-10 mb-3 opacity-40" />
                  <p className="mb-4 text-sm">
                    {topic === "property"
                      ? (propertyAddress.trim() ? `Ask about: ${propertyAddress.trim()}` : TOPIC_HINTS.property)
                      : (TOPIC_HINTS[topic] || "Ask a general property question.")}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center max-w-md">
                    {STARTERS.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-[#547d91] hover:text-[#547d91] transition-colors"
                        data-testid="advisor-starter"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((m) => (
                  <div
                    key={m.message_id}
                    className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {m.role === "assistant" && (
                      <div className="w-7 h-7 rounded-full bg-[#547d91] flex items-center justify-center shrink-0">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-4 py-2.5 max-w-[80%] text-sm ${
                        m.role === "user"
                          ? "bg-[#547d91] text-white"
                          : "bg-white border border-slate-200 text-slate-800"
                      }`}
                    >
                      {m.role === "assistant" ? (
                        <div className="advisor-md text-slate-800">
                          <ReactMarkdown>{m.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <span className="whitespace-pre-wrap">{m.content}</span>
                      )}
                    </div>
                    {m.role === "user" && (
                      <div className="w-7 h-7 rounded-full bg-slate-300 flex items-center justify-center shrink-0">
                        <UserIcon className="w-4 h-4 text-slate-600" />
                      </div>
                    )}
                  </div>
                ))
              )}

              {sending && (
                <div className="flex gap-2 justify-start">
                  <div className="w-7 h-7 rounded-full bg-[#547d91] flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="rounded-2xl px-4 py-3 bg-white border border-slate-200">
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-slate-200 p-3 bg-white flex items-center gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                placeholder="Ask the AI advisor..."
                disabled={sending}
                data-testid="advisor-input"
              />
              <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="advisor-send">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
